import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Icon } from '~/global/components/icon/Icon';
import { cn } from '@/lib/utils';
import { WorkoutSummaryData, WorkoutSummaryUtils } from '@fytness/shared';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import type {
  WorkoutRecord,
  WorkoutSetValueRecord,
  WorkoutExerciseDimensionRecord,
  WorkoutSetRecord,
  WorkoutExerciseRecord,
  RecordedSessionRecord,
} from '~/powersync/generated/schema';

type IconName = React.ComponentProps<typeof Icon>['name'];

/** A single stat tile inside the summary stats card. */
const StatTile = ({
  icon,
  value,
  label,
}: {
  icon: IconName;
  value: string;
  label: string;
}) => (
  <View className="flex-1 gap-1 rounded-xl bg-surface-elevated p-4">
    <Icon name={icon} size={16} className="text-accent" />
    <Text className="text-2xl font-bold text-text-primary">{value}</Text>
    <Text className="text-xs text-text-muted">{label}</Text>
  </View>
);

interface DifficultyLevel {
  value: number;
  icon: IconName;
  key: string;
}

const DIFFICULTY_LEVELS: DifficultyLevel[] = [
  { value: 1, icon: 'Feather', key: 'easy' },
  { value: 2, icon: 'Smile', key: 'ok' },
  { value: 3, icon: 'Target', key: 'right' },
  { value: 4, icon: 'Flame', key: 'hard' },
  { value: 5, icon: 'Zap', key: 'max' },
];

// "Správne" is the sensible default highlighted before the user rates the workout
const DEFAULT_DIFFICULTY = 3;

export interface WorkoutSummaryProps {
  /**
   * The calculated workout summary data
   */
  summary: WorkoutSummaryData;
  /**
   * Name of the finished workout (shown in the subtitle)
   */
  workoutName: string;
  /**
   * Free-text note for the workout
   */
  note: string;
  /**
   * Perceived difficulty (1-5), or null if not yet rated
   */
  difficulty: number | null;
  /**
   * Called when the user picks a difficulty level
   */
  onChangeDifficulty: (level: number) => void;
  /**
   * Called when the user finishes editing the note
   */
  onChangeNote: (text: string) => void;
  /**
   * Called when the user taps the "back to overview" button
   */
  onDone: () => void;
  /**
   * The device-recorded session attached to this workout, if any (#29).
   */
  recordedSession?: RecordedSessionRecord | null;
  /**
   * Additional CSS classes
   */
  className?: string;
}

export const WorkoutSummary: React.FC<WorkoutSummaryProps> & {
  Synced: typeof WorkoutSummarySynced;
} = ({
  summary,
  workoutName,
  note,
  difficulty,
  onChangeDifficulty,
  onChangeNote,
  onDone,
  recordedSession,
  className,
}) => {
  const { t } = useTranslation('workout');
  const [noteDraft, setNoteDraft] = useState(note);

  useEffect(() => {
    setNoteDraft(note);
  }, [note]);

  return (
    <View testID="workout-summary" className={cn('gap-4', className)}>
      {/* Header */}
      <View className="items-center gap-3 py-2">
        <View className="h-20 w-20 items-center justify-center rounded-full bg-positive">
          <Icon name="Check" size={40} className="text-positive-foreground" />
        </View>
        <Text className="text-2xl font-bold text-text-primary">
          🏋️ {t('summary.title')}
        </Text>
        <Text className="text-text-secondary">
          {t('summary.subtitle', { name: workoutName })}
        </Text>
      </View>

      {/* Stats + difficulty side by side on desktop */}
      <View className="gap-4 md:flex-row md:items-stretch">
        {/* Stats */}
        <Card className="bg-surface md:flex-1">
          <CardContent className="gap-4 px-6">
            <View className="flex-row items-center gap-2">
              <Icon name="ChartColumn" size={20} className="text-accent" />
              <Text className="text-lg font-semibold text-text-primary">
                {t('summary.stats.title')}
              </Text>
            </View>
            {/* Volume — the hero stat */}
            <View className="gap-1 rounded-xl bg-surface-elevated p-4">
              <Text className="text-sm text-text-secondary">
                {t('summary.stats.volume')}
              </Text>
              <Text className="text-2xl font-bold text-text-primary">
                {WorkoutSummaryUtils.formatVolume(summary.totalVolume)}{' '}
                {t('summary.units.kg')}
              </Text>
              <Text className="text-xs text-text-muted">
                {t('summary.stats.volumeCaption')}
              </Text>
            </View>

            {recordedSession ? (
              <>
                <View className="flex-row gap-3">
                  <StatTile
                    icon="Timer"
                    value={WorkoutSummaryUtils.formatDuration(
                      summary.totalDuration,
                    )}
                    label={t('summary.stats.duration')}
                  />
                  <StatTile
                    icon="HeartPulse"
                    value={
                      recordedSession.avgHeartRate == null
                        ? '—'
                        : t('recorded.match.bpm', {
                            bpm: recordedSession.avgHeartRate,
                          })
                    }
                    label={t('recorded.avgHr')}
                  />
                </View>
                <View className="flex-row gap-3">
                  <StatTile
                    icon="HeartPulse"
                    value={
                      recordedSession.maxHeartRate == null
                        ? '—'
                        : t('recorded.match.bpm', {
                            bpm: recordedSession.maxHeartRate,
                          })
                    }
                    label={t('recorded.maxHr')}
                  />
                  <StatTile
                    icon="Flame"
                    value={
                      recordedSession.activeCalories == null
                        ? '—'
                        : t('recorded.match.kcal', {
                            kcal: Math.round(recordedSession.activeCalories),
                          })
                    }
                    label={t('recorded.calories')}
                  />
                </View>
                <View className="flex-row items-center gap-2">
                  <Icon
                    name="HeartPulse"
                    size={14}
                    className="text-text-muted"
                  />
                  <Text className="text-xs text-text-muted">
                    {t('recorded.fromSource', {
                      source: t(
                        recordedSession.source === 'HEALTH_CONNECT'
                          ? 'recorded.sourceHealthConnect'
                          : 'recorded.sourceAppleHealth',
                      ),
                    })}
                  </Text>
                </View>
              </>
            ) : (
              <StatTile
                icon="Timer"
                value={WorkoutSummaryUtils.formatDuration(
                  summary.totalDuration,
                )}
                label={t('summary.stats.duration')}
              />
            )}
          </CardContent>
        </Card>

        {/* Difficulty (náročnosť) */}
        <Card className="bg-surface md:flex-1">
          <CardContent className="gap-4 px-6">
            <View className="flex-row items-center gap-2">
              <Icon name="Gauge" size={20} className="text-accent" />
              <Text className="text-lg font-semibold text-text-primary">
                {t('summary.difficulty.title')}
              </Text>
            </View>
            <View className="flex-row gap-2">
              {DIFFICULTY_LEVELS.map((level) => {
                const selected = difficulty === level.value;
                return (
                  <Pressable
                    key={level.value}
                    onPress={() => onChangeDifficulty(level.value)}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    className={cn(
                      'flex-1 items-center gap-1.5 rounded-xl border p-2.5 active:opacity-70',
                      selected
                        ? 'border-accent bg-accent-soft'
                        : 'border-transparent bg-surface-elevated',
                    )}
                  >
                    <Icon
                      name={level.icon}
                      size={20}
                      className={selected ? 'text-accent' : 'text-text-muted'}
                    />
                    <Text
                      className={cn(
                        'text-xs font-medium',
                        selected ? 'text-accent' : 'text-text-muted',
                      )}
                    >
                      {t(`summary.difficulty.levels.${level.key}`)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </CardContent>
        </Card>
      </View>

      {/* Note (poznámka) */}
      <Card className="bg-surface">
        <CardContent className="gap-3 px-6">
          <View className="flex-row items-center gap-2">
            <Icon name="MessageSquare" size={20} className="text-accent" />
            <Text className="text-lg font-semibold text-text-primary">
              {t('summary.note.title')}
            </Text>
          </View>
          <Input
            multiline
            value={noteDraft}
            onChangeText={setNoteDraft}
            onBlur={() => onChangeNote(noteDraft)}
            placeholder={t('summary.note.placeholder')}
            textAlignVertical="top"
            className="min-h-24 border-0 bg-surface-elevated py-3"
          />
        </CardContent>
      </Card>

      {/* Back to overview */}
      <View className="flex-row justify-center pt-2">
        <Button
          variant="primary"
          onPress={onDone}
          className="flex-row gap-2 px-8"
        >
          <Icon name="House" size={18} />
          <Text className="font-semibold">{t('summary.done')}</Text>
        </Button>
      </View>
    </View>
  );
};

export interface WorkoutSummarySyncedProps {
  /**
   * The workout ID to calculate summary for
   */
  workoutId: string;
  /**
   * Additional CSS classes
   */
  className?: string;
}

const WorkoutSummarySynced: React.FC<WorkoutSummarySyncedProps> = ({
  workoutId,
  className,
}) => {
  const powerSync = usePowerSync();
  const router = useRouter();

  // Fetch workout basic data
  const { data: workout, isLoading: isLoadingWorkout } =
    useLivePowerSyncQuery<WorkoutRecord>(
      db.Workout.queries.findById(workoutId),
      [workoutId],
      { single: true },
    );

  const isFinished = workout?.finishedAt;

  // Fetch all set values for this workout
  const { data: setValues, isLoading: isLoadingSetValues } =
    useLivePowerSyncQuery<WorkoutSetValueRecord>(
      isFinished ? db.Workout.queries.findSetValuesByWorkout(workoutId) : null,
      [workoutId],
    );

  // Fetch all exercise dimensions for this workout
  const { data: exerciseDimensions, isLoading: isLoadingDimensions } =
    useLivePowerSyncQuery<WorkoutExerciseDimensionRecord>(
      isFinished ? db.Workout.queries.findDimensionsByWorkout(workoutId) : null,
      [workoutId],
    );

  // Fetch all sets for this workout
  const { data: sets, isLoading: isLoadingSets } =
    useLivePowerSyncQuery<WorkoutSetRecord>(
      isFinished ? db.Workout.queries.findSetsByWorkout(workoutId) : null,
      [workoutId],
    );

  // Fetch all exercises for this workout
  const { data: exercises, isLoading: isLoadingExercises } =
    useLivePowerSyncQuery<WorkoutExerciseRecord>(
      isFinished ? db.Workout.queries.findExercisesByWorkout(workoutId) : null,
      [workoutId],
    );

  // The device-recorded session attached to this workout, if any (#29).
  const { data: recordedSessions } =
    useLivePowerSyncQuery<RecordedSessionRecord>(
      isFinished ? db.RecordedSession.queries.findByWorkout(workoutId) : null,
      [workoutId],
    );

  const summary = useMemo(() => {
    if (
      !isFinished ||
      !workout ||
      !setValues ||
      !exerciseDimensions ||
      !sets ||
      !exercises
    ) {
      return null;
    }

    // Get unique exercise IDs
    const exerciseIds = exercises.map((exercise) => exercise.id);

    // Calculate summary using the utility
    return WorkoutSummaryUtils.calculateSummary({
      workout: {
        id: workout.id,
        startedAt: workout.startedAt ? new Date(workout.startedAt) : null,
        finishedAt: workout.finishedAt ? new Date(workout.finishedAt) : null,
      },
      setValues: setValues.map((value) => ({
        id: value.id,
        setId: value.setId,
        exerciseDimensionId: value.exerciseDimensionId,
        value: Number(value.value),
        workoutId: value.workoutId,
      })),
      exerciseDimensions: exerciseDimensions.map((dim) => ({
        id: dim.id,
        exerciseId: dim.exerciseId,
        dimension: dim.dimension as any,
        unit: dim.unit,
        workoutId: dim.workoutId,
      })),
      sets: sets.map((set) => ({
        id: set.id,
        completedAt: set.completedAt ? new Date(set.completedAt) : null,
      })),
      exerciseIds,
    });
  }, [workout, setValues, exerciseDimensions, sets, exercises, isFinished]);

  const isLoading =
    isLoadingWorkout ||
    (isFinished &&
      (isLoadingSetValues ||
        isLoadingDimensions ||
        isLoadingSets ||
        isLoadingExercises));

  if (!isFinished) {
    return null; // Don't show summary for unfinished workouts
  }

  if (isLoading || !summary || !workout) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <View className="mb-6 items-center">
            <View className="bg-muted mb-2 h-8 w-48 rounded" />
            <View className="bg-muted h-4 w-64 rounded" />
          </View>
          <View className="gap-4">
            {[1, 2, 3, 4].map((i) => (
              <View
                key={i}
                className="bg-muted/30 flex-row items-center justify-between rounded-lg p-4"
              >
                <View className="flex-row items-center gap-3">
                  <View className="bg-muted h-6 w-6 rounded" />
                  <View>
                    <View className="bg-muted mb-1 h-4 w-20 rounded" />
                    <View className="bg-muted h-6 w-16 rounded" />
                  </View>
                </View>
              </View>
            ))}
          </View>
        </CardContent>
      </Card>
    );
  }

  const handleChangeDifficulty = (level: number) => {
    void db.Workout.mutations.update(powerSync, workoutId, {
      difficulty: level,
    });
  };

  const handleChangeNote = (text: string) => {
    void db.Workout.mutations.update(powerSync, workoutId, { note: text });
  };

  return (
    <WorkoutSummary
      summary={summary}
      workoutName={workout.name ?? ''}
      note={workout.note ?? ''}
      difficulty={workout.difficulty ?? DEFAULT_DIFFICULTY}
      onChangeDifficulty={handleChangeDifficulty}
      onChangeNote={handleChangeNote}
      onDone={() => router.replace('/(overview)')}
      recordedSession={recordedSessions[0] ?? null}
      className={className}
    />
  );
};

// Attach Synced as a static property
WorkoutSummary.Synced = WorkoutSummarySynced;
